import type {CopilotChatOrg} from '@github-ui/copilot-chat-types'
import {generateSSOUrl} from '@github-ui/global-sso-banner-ui/generate-sso-url'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation} from '@github-ui/react-router'
import {Banner} from '@primer/react'
import {useEffect} from 'react'

import styles from './NotificationsSection.module.css'

/**
 * Matches text that looks like an owner/repo reference (e.g. "github/copilot-api").
 *
 * - Owner and repo are letters, digits, dashes, or underscores only (no dots).
 * - Must not be preceded or followed by another path segment, dot, or word character.
 *   So "github/github" matches, but "github/github/foo" and "github/github.md" do not.
 *
 * Uses a leading non-capturing group instead of a lookbehind for broader browser support.
 */
const OWNER_REPO_PATTERN = /(?:^|[^a-zA-Z0-9\-_./])([a-zA-Z0-9\-_]+)\/[a-zA-Z0-9\-_]+(?![a-zA-Z0-9\-_./])/

/**
 * Extracts a potential org owner from user input text that looks like an owner/repo reference.
 */
export function extractOwnerFromInput(inputText: string): string | null {
  const match = inputText.match(OWNER_REPO_PATTERN)
  return match?.[1] ?? null
}

/**
 * Finds the SSO organization that matches the owner extracted from input text.
 */
export function findMatchingSsoOrg(inputText: string, ssoOrganizations: CopilotChatOrg[]): CopilotChatOrg | null {
  if (!ssoOrganizations.length || !inputText) return null

  const owner = extractOwnerFromInput(inputText)
  if (!owner) return null

  return ssoOrganizations.find(org => org.login.toLowerCase() === owner.toLowerCase()) ?? null
}

interface SsoAuthBannerProps {
  matchingOrg: CopilotChatOrg
}

export function SsoAuthBanner({matchingOrg}: SsoAuthBannerProps) {
  const location = useLocation()

  useEffect(() => {
    sendEvent('dotcom_chat.activate', {
      target: 'PROACTIVE_SSO_AUTH_BANNER',
      action: 'shown',
      mode: 'immersive',
    })
  }, [matchingOrg.login])

  return (
    <div className={styles.banner}>
      <Banner
        title={`Authenticate with SSO to access ${matchingOrg.login}`}
        hideTitle
        description={
          <>
            Single sign-on to access resources in <strong>{matchingOrg.login}</strong>.
          </>
        }
        primaryAction={
          <Banner.PrimaryAction
            as="a"
            href={generateSSOUrl(matchingOrg.login, location)}
            aria-label={`Single sign-on for ${matchingOrg.login}`}
            onClick={() => {
              sendEvent('dotcom_chat.activate', {
                target: 'PROACTIVE_SSO_AUTH_BANNER',
                action: 'click',
                mode: 'immersive',
              })
            }}
          >
            Single sign-on
          </Banner.PrimaryAction>
        }
      />
    </div>
  )
}
